import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Button, Tag, Space, Popconfirm, message, Select, Form, InputNumber, Modal, Switch, Divider, Row, Col, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, SwapRightOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api-client';
import { LabelWithHelp } from '../../components/ui/HelpTooltip';

const { Text } = Typography;

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
}

interface City {
  id: number;
  regionId: number;
  nameUz: string;
  nameRu: string;
}

interface RoutePricing {
  id: number;
  fromRegionId: number;
  toRegionId: number;
  tripType: 'INTERCITY_TAXI' | 'INTERREGION_TAXI' | 'POSTAGE' | 'CARGO';
  basePrice: number;
  pricePerKm: number;
  minPrice: number;
  maxPrice: number | null;
  surgeMultiplier: number;
  isActive: boolean;
  fromRegion?: Region;
  toRegion?: Region;
}

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida taksi',
  INTERREGION_TAXI: 'Shaharlararo taksi',
  CARGO: 'Yuk tashish',
};

const tripTypeColors: Record<string, string> = {
  INTERCITY_TAXI: 'cyan',
  INTERREGION_TAXI: 'blue',
  CARGO: 'orange',
};

export const RoutePricingList = () => {
  const { create } = useNavigation();
  const [regions, setRegions] = useState<Region[]>([]);
  const [fromCities, setFromCities] = useState<City[]>([]);
  const [toCities, setToCities] = useState<City[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RoutePricing | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const { tableProps, tableQueryResult } = useTable<RoutePricing>({
    resource: 'admin/pricing/routes',
    syncWithLocation: true,
  });

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const response = await apiClient.get('/admin/regions');
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setRegions(data);
    } catch {
      message.error('Viloyatlarni yuklashda xatolik');
    }
  };

  const fetchCities = async (regionId: number, target: 'from' | 'to') => {
    try {
      const response = await apiClient.get('/admin/cities', { params: { regionId } });
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      if (target === 'from') setFromCities(data);
      else setToCities(data);
    } catch {
      if (target === 'from') setFromCities([]);
      else setToCities([]);
    }
  };

  const handleCreate = () => {
    setEditingRecord(null);
    setFromCities([]);
    setToCities([]);
    form.resetFields();
    form.setFieldsValue({ isActive: true, surgeMultiplier: 1.0 });
    setIsModalOpen(true);
  };

  const handleEdit = (record: RoutePricing) => {
    setEditingRecord(record);
    form.setFieldsValue({
      fromRegionId: record.fromRegionId,
      toRegionId: record.toRegionId,
      tripType: record.tripType,
      basePrice: record.basePrice,
      pricePerKm: record.pricePerKm,
      minPrice: record.minPrice,
      maxPrice: record.maxPrice,
      surgeMultiplier: Number(record.surgeMultiplier) || 1.0,
      isActive: record.isActive,
    });
    // Load cities for both regions
    fetchCities(record.fromRegionId, 'from');
    fetchCities(record.toRegionId, 'to');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/pricing/routes/${id}`);
      message.success('Narx o\'chirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await apiClient.put(`/admin/pricing/routes/${id}/toggle`);
      message.success('Holat o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const payload = {
        fromRegionId: values.fromRegionId,
        toRegionId: values.toRegionId,
        tripType: values.tripType,
        basePrice: values.basePrice,
        pricePerKm: values.pricePerKm,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
        surgeMultiplier: values.surgeMultiplier || 1.0,
        isActive: values.isActive,
      };

      if (editingRecord) {
        await apiClient.put(`/admin/pricing/routes/${editingRecord.id}`, payload);
        message.success('Narx yangilandi');
      } else {
        await apiClient.post('/admin/pricing/routes', payload);
        message.success('Narx yaratildi');
      }
      setIsModalOpen(false);
      tableQueryResult.refetch();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  const getSurgeColor = (multiplier: number) => {
    if (multiplier >= 2) return 'red';
    if (multiplier >= 1.5) return 'orange';
    if (multiplier > 1) return 'gold';
    return 'green';
  };

  const columns = [
    {
      title: 'Yo\'nalish',
      key: 'route',
      render: (_: unknown, record: RoutePricing) => (
        <Space>
          <Text strong>{record.fromRegion?.nameUz || `ID: ${record.fromRegionId}`}</Text>
          <SwapRightOutlined style={{ color: '#1ABC9C' }} />
          <Text strong>{record.toRegion?.nameUz || `ID: ${record.toRegionId}`}</Text>
        </Space>
      ),
    },
    {
      title: 'Sayohat turi',
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string) => (
        <Tag color={tripTypeColors[type] || 'blue'}>{tripTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: 'Boshlang\'ich',
      dataIndex: 'basePrice',
      key: 'basePrice',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Km uchun',
      dataIndex: 'pricePerKm',
      key: 'pricePerKm',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Min narx',
      dataIndex: 'minPrice',
      key: 'minPrice',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Max narx',
      key: 'maxPrice',
      render: (_: unknown, record: RoutePricing) =>
        record.maxPrice ? formatCurrency(record.maxPrice) : <Text type="secondary">-</Text>,
    },
    {
      title: 'Surge',
      key: 'surgeMultiplier',
      render: (_: unknown, record: RoutePricing) => {
        const val = Number(record.surgeMultiplier) || 1;
        return (
          <Tag color={getSurgeColor(val)} icon={val > 1 ? <ThunderboltOutlined /> : undefined}>
            x{val.toFixed(2)}
          </Tag>
        );
      },
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean, record: RoutePricing) => (
        <Switch
          checked={active}
          onChange={() => handleToggle(record.id)}
          checkedChildren="Faol"
          unCheckedChildren="Nofaol"
        />
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: RoutePricing) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="O'chirishni tasdiqlaysizmi?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title="Yo'nalish narxlari"
        headerButtons={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Yangi narx
          </Button>
        }
      >
        <Table {...tableProps} columns={columns} rowKey="id" />
      </List>

      <Modal
        title={editingRecord ? 'Narxni tahrirlash' : 'Yangi narx qo\'shish'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true, surgeMultiplier: 1.0 }}
        >
          {/* ===== ROUTE SELECTION ===== */}
          <Divider orientation="left" plain>Yo'nalish</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fromRegionId"
                label={<LabelWithHelp label="Qayerdan (Viloyat)" help="Yo'lovchi chiqadigan viloyatni tanlang" />}
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => {
                    if (val) fetchCities(val, 'from');
                    else setFromCities([]);
                  }}
                >
                  {regions.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.nameUz}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {fromCities.length > 0 && (
                <div style={{ marginTop: -12, marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Shaharlar: {fromCities.map(c => c.nameUz).join(', ')}</Text>
                </div>
              )}
            </Col>
            <Col span={12}>
              <Form.Item
                name="toRegionId"
                label={<LabelWithHelp label="Qayerga (Viloyat)" help="Yo'lovchi boradigan viloyatni tanlang" />}
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => {
                    if (val) fetchCities(val, 'to');
                    else setToCities([]);
                  }}
                >
                  {regions.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.nameUz}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {toCities.length > 0 && (
                <div style={{ marginTop: -12, marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Shaharlar: {toCities.map(c => c.nameUz).join(', ')}</Text>
                </div>
              )}
            </Col>
          </Row>

          <Form.Item
            name="tripType"
            label="Sayohat turi"
            rules={[{ required: true, message: 'Sayohat turini tanlang' }]}
          >
            <Select placeholder="Sayohat turini tanlang">
              <Select.Option value="INTERREGION_TAXI">Shaharlararo taksi</Select.Option>
              <Select.Option value="INTERCITY_TAXI">Shahar ichida taksi</Select.Option>
              <Select.Option value="CARGO">Yuk tashish</Select.Option>
            </Select>
          </Form.Item>

          {/* ===== PRICING INPUTS ===== */}
          <Divider orientation="left" plain>Narxlash</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="basePrice"
                label={<LabelWithHelp label="Boshlang'ich narx (so'm)" help="Masofadan qat'i nazar olinadigan asosiy narx" />}
                rules={[{ required: true, message: 'Narxni kiriting' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(value) => value!.replace(/\s?/g, '')}
                  min={0}
                  placeholder="50 000"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="pricePerKm"
                label={<LabelWithHelp label="Har bir km uchun narx (so'm)" help="Har bir kilometr uchun qo'shiladigan narx" />}
                rules={[{ required: true, message: 'Narxni kiriting' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(value) => value!.replace(/\s?/g, '')}
                  min={0}
                  placeholder="2 000"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="minPrice"
                label={<LabelWithHelp label="Minimal narx (so'm)" help="Yo'lovchiga ko'rsatiladigan eng kam narx" />}
                rules={[{ required: true, message: 'Minimal narxni kiriting' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(value) => value!.replace(/\s?/g, '')}
                  min={0}
                  placeholder="80 000"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxPrice"
                label={<LabelWithHelp label="Maksimal narx (so'm)" help="Yo'lovchiga ko'rsatiladigan eng ko'p narx" />}
                rules={[{ required: true, message: 'Maksimal narxni kiriting' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(value) => value!.replace(/\s?/g, '')}
                  min={0}
                  placeholder="200 000"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ===== SURGE COEFFICIENT ===== */}
          <Divider orientation="left" plain>
            <ThunderboltOutlined /> Surge koeffitsienti
          </Divider>

          <Form.Item
            name="surgeMultiplier"
            label={<LabelWithHelp label="Surge koeffitsienti" help="Narxni ko'paytiruvchi koeffitsient. 1.0 = oddiy narx, 1.5 = 50% qimmatroq, 2.0 = 2 baravar qimmat" />}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={5}
              step={0.1}
              placeholder="1.0"
              addonAfter={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {form.getFieldValue('surgeMultiplier') > 1
                    ? `+${Math.round((form.getFieldValue('surgeMultiplier') - 1) * 100)}% qo'shimcha`
                    : 'Oddiy narx'}
                </Text>
              }
            />
          </Form.Item>

          <Divider />

          <Form.Item name="isActive" valuePropName="checked">
            <Switch checkedChildren="Faol" unCheckedChildren="Nofaol" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingRecord ? 'Saqlash' : 'Yaratish'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>Bekor qilish</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
